/*
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await complaintService.getAdminStats();
      setStats(data.stats);
    } catch (err) {
      toast.error('Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!stats) return null;

  return (
    <div className="max-w-5xl mx-auto mt-6 px-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Admin Dashboard</h2>
        <button
          onClick={() => navigate('/admin/complaints')}
          className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          Manage Complaints
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total Complaints" value={stats.totalComplaints} />
        <StatCard label="Pending" value={stats.pendingComplaints} />
        <StatCard label="In Progress" value={stats.inProgressComplaints} />
        <StatCard label="Resolved" value={stats.resolvedComplaints} />
        <StatCard label="Users" value={stats.totalUsers} />
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <h3 className="font-semibold text-sm mb-2">Complaints by Category</h3>
        {(!stats.complaintsByCategory ||
          stats.complaintsByCategory.length === 0) && (
          <p className="text-xs text-gray-500">No data yet</p>
        )}
        <ul className="text-sm">
          {stats.complaintsByCategory?.map((c) => (
            <li key={c._id} className="flex justify-between py-1 text-gray-700">
              <span>{c._id}</span>
              <span>{c.count}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-sm mb-2">Recent Complaints</h3>
        {(!stats.recentComplaints || stats.recentComplaints.length === 0) && (
          <p className="text-xs text-gray-500">No complaints yet</p>
        )}
        <ul className="space-y-2 text-sm">
          {stats.recentComplaints?.map((c) => (
            <li
              key={c._id}
              className="flex justify-between items-center border-b pb-1 last:border-b-0"
            >
              <div>
                <p className="font-medium">{c.title}</p>
                <p className="text-xs text-gray-500">
                  {c.user?.name} •{' '}
                  {new Date(c.createdAt).toLocaleDateString()}
                </p>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-gray-100">
                {c.status}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-2xl font-semibold">{value}</p>
  </div>
);

export default AdminDashboard;
*/

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await complaintService.getAdminStats();
      setStats(data.stats);
    } catch (err) {
      toast.error('Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!stats) return null;

  return (
    <div className="max-w-6xl mx-auto mt-6 px-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Admin Control Center
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Overview of citizen complaints and cleanliness operations.
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/complaints')}
          className="px-4 py-2 text-xs sm:text-sm bg-indigo-600 text-white rounded-full hover:bg-indigo-700 shadow-sm"
        >
          View &amp; Manage Complaints
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-5">
        <StatCard
          label="Total Complaints"
          value={stats.totalComplaints}
          color="from-sky-500 to-indigo-500"
          icon="🧾"
        />
        <StatCard
          label="Pending"
          value={stats.pendingComplaints}
          color="from-amber-400 to-orange-500"
          icon="⏳"
        />
        <StatCard
          label="In Progress"
          value={stats.inProgressComplaints}
          color="from-blue-400 to-blue-600"
          icon="⚙️"
        />
        <StatCard
          label="Resolved"
          value={stats.resolvedComplaints}
          color="from-emerald-400 to-green-600"
          icon="✅"
        />
        <StatCard
          label="Registered Users"
          value={stats.totalUsers}
          color="from-slate-400 to-slate-600"
          icon="👥"
        />
      </div>

      {/* Category distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl shadow-md p-5 border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">
            Complaints by Category
          </h3>
          {(!stats.complaintsByCategory ||
            stats.complaintsByCategory.length === 0) && (
            <p className="text-xs text-gray-500">
              No data yet. Complaints will appear here once citizens start
              reporting.
            </p>
          )}
          <ul className="space-y-2 text-sm">
            {stats.complaintsByCategory?.map((c) => (
              <li
                key={c._id}
                className="flex items-center justify-between text-gray-700"
              >
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-indigo-500" />
                  <span className="capitalize">
                    {c._id || 'Uncategorized'}
                  </span>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                  {c.count}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Recent complaints */}
        <div className="bg-white rounded-2xl shadow-md p-5 border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">
            Recent Complaints
          </h3>
          {(!stats.recentComplaints ||
            stats.recentComplaints.length === 0) && (
            <p className="text-xs text-gray-500">
              No complaints yet. They will appear here in reverse order.
            </p>
          )}
          <ul className="space-y-2 text-sm max-h-64 overflow-y-auto">
            {stats.recentComplaints?.map((c) => (
              <li
                key={c._id}
                className="flex justify-between items-start border-b pb-2 last:border-b-0 last:pb-0"
              >
                <div>
                  <p className="font-medium text-gray-800 line-clamp-1">
                    {c.title}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {c.user?.name || 'Unknown'} •{' '}
                    {new Date(c.createdAt).toLocaleDateString()}
                  </p>
                  <p className="text-[11px] text-gray-500 capitalize">
                    {c.category} • Priority: {c.priority}
                  </p>
                </div>
                <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200">
                  {c.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, icon }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <div className="h-8 w-8 rounded-xl bg-gradient-to-br flex items-center justify-center text-lg text-white shadow-sm">
        <span role="img" aria-label={label} className={`inline-block`}>
          {icon}
        </span>
      </div>
      <span className="text-xs text-gray-500 text-right">{label}</span>
    </div>
    <p className="text-xl font-semibold text-gray-900 mt-1">
      {value ?? 0}
    </p>
  </div>
);

export default AdminDashboard;
